"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import { MapPin, Calendar, FileText } from "lucide-react";

interface InTransitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    location: string;
    estimated_delivery_date: string;
    notes?: string;
  }) => void;
  loading?: boolean;
}

export function InTransitModal({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}: InTransitModalProps) {
  const [formData, setFormData] = useState({
    location: "",
    estimated_delivery_date: "",
    notes: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.location.trim()) {
      newErrors.location = "Current location is required";
    }

    if (!formData.estimated_delivery_date) {
      newErrors.estimated_delivery_date = "Estimated delivery date is required";
    } else {
      const selectedDate = new Date(formData.estimated_delivery_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.estimated_delivery_date = "Delivery date cannot be in the past";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    onSubmit({
      location: formData.location.trim(),
      estimated_delivery_date: formData.estimated_delivery_date,
      notes: formData.notes.trim() || undefined,
    });
  };

  const handleClose = () => {
    setFormData({
      location: "",
      estimated_delivery_date: "",
      notes: "",
    });
    setErrors({});
    onClose();
  };

  // Get today's date in YYYY-MM-DD format for min attribute
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-card border border-border/40 shadow-2xl">
        <DialogHeader className="pb-4 border-b border-border/60">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-md">
              <MapPin className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle className="text-lg font-semibold text-foreground">
              Update In-Transit Status
            </DialogTitle>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Provide current location and expected delivery information
          </p>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <StandardFormInput
            label="Current Location"
            required
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            placeholder="e.g., Delhi Hub, Mumbai Sorting Center"
            error={errors.location}
            helperText="Current location of the parcel"
          />

          <div>
            <label className="block text-[13px] font-normal text-muted-foreground mb-1">
              Estimated Delivery Date
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                type="date"
                value={formData.estimated_delivery_date}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    estimated_delivery_date: e.target.value,
                  })
                }
                min={getTodayDate()}
                className="w-full px-4 py-2 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            </div>
            {errors.estimated_delivery_date && (
              <p className="text-xs text-red-500 mt-1">
                {errors.estimated_delivery_date}
              </p>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">
              Expected date of delivery to customer
            </p>
          </div>

          <div>
            <label className="block text-[13px] font-normal text-muted-foreground mb-1">
              Additional Notes
            </label>
            <div className="relative">
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Any additional information about the shipment status..."
                rows={3}
                className="w-full px-4 py-2 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Optional - Any relevant updates or remarks
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <FileText className="h-3 w-3 inline mr-1" />
              The customer will be notified about the updated status and estimated delivery date.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-border/60">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            className="text-sm h-9 px-4"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="text-sm h-9 px-4 bg-primary hover:bg-primary/90"
          >
            {loading ? "Updating..." : "Update Status"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
